/**
 * Custom hook for Bulk Paste Import functionality
 * Handles parsing, validation, and import of pasted session data
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { OBSERVER_RATE_PER_MIN, PRINCIPAL_RATE_PER_SESSION } from './useSessionHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useBulkPasteImport = (filterOptions, toast, onImportComplete) => {
  const [bulkPasteData, setBulkPasteData] = useState('');
  const [parsedSessions, setParsedSessions] = useState([]);
  const [parseError, setParseError] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Parse bulk paste data
  const parseBulkPaste = useCallback((data) => {
    if (!data.trim()) {
      setParsedSessions([]);
      setParseError('');
      return;
    }
    
    try {
      const lines = data.trim().split('\n').filter(line => line.trim());
      const parsed = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        let childName = '';
        let observerName = '';
        let sessionDate = '';
        let jsonData = {};
        
        // Try to extract JSON from the line
        const jsonMatch = line.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            jsonData = JSON.parse(jsonMatch[0]);
          } catch (e) {
            console.warn('Failed to parse JSON in line', i, e);
          }
          
          // Get the parts before JSON
          const beforeJson = line.substring(0, line.indexOf('{')).trim();
          const parts = beforeJson.split('\t').filter(p => p.trim());
          
          if (parts.length >= 1) childName = parts[0].trim();
          if (parts.length >= 2) observerName = parts[1].trim();
          if (parts.length >= 3) sessionDate = parts[2].trim();
        } else {
          // No JSON, just tab-separated values
          const parts = line.split('\t').filter(p => p.trim());
          if (parts.length >= 1) childName = parts[0].trim();
          if (parts.length >= 2) observerName = parts[1].trim();
          if (parts.length >= 3) sessionDate = parts[2].trim();
        }
        
        // Parse date (handle DD-MM-YYYY format)
        if (sessionDate && sessionDate.includes('-')) {
          const dateParts = sessionDate.split('-');
          if (dateParts[0].length === 2) {
            // Convert DD-MM-YYYY to YYYY-MM-DD
            sessionDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
          }
        }
        
        // Try to match child and observer
        const matchedChild = filterOptions.children.find(c => 
          c.name.toLowerCase().includes(childName.toLowerCase()) ||
          childName.toLowerCase().includes(c.name.split(' ')[0].toLowerCase())
        );
        
        const matchedObserver = filterOptions.observers.find(o => 
          o.name.toLowerCase().includes(observerName.toLowerCase()) ||
          observerName.toLowerCase().includes(o.name.split(' ')[0].toLowerCase())
        );
        
        // Extract content
        const transcript = jsonData?.transcript || '';
        const report = jsonData?.report || jsonData?.formatted_report || '';
        const callAnalysis = jsonData?.communication_review || jsonData?.call_analysis || '';
        
        // Calculate duration and earnings
        const wordCount = transcript.split(/\s+/).length;
        const estimatedMinutes = Math.max(5, Math.ceil(wordCount / 150));
        
        // Calculate earnings
        const observerEarnings = estimatedMinutes * OBSERVER_RATE_PER_MIN;
        const principalEarnings = PRINCIPAL_RATE_PER_SESSION;
        
        parsed.push({
          rawLine: line.substring(0, 100) + (line.length > 100 ? '...' : ''),
          childName,
          childId: matchedChild?.id || '',
          childMatched: !!matchedChild,
          observerName,
          observerId: matchedObserver?.id || '',
          observerMatched: !!matchedObserver,
          sessionDate,
          transcript,
          report,
          callAnalysis,
          durationMinutes: estimatedMinutes,
          observerEarnings,
          principalEarnings,
          totalEarnings: observerEarnings + principalEarnings,
          hasData: !!transcript || !!report || !!callAnalysis
        });
      }
      
      setParsedSessions(parsed);
      
      // Show warning for unmatched
      const unmatched = parsed.filter(p => !p.childMatched || !p.observerMatched);
      if (unmatched.length > 0) {
        setParseError(`${unmatched.length} session(s) need attention - child or observer not matched`);
      } else {
        setParseError('');
      }
    } catch (error) {
      console.error('Parse error:', error);
      setParseError('Failed to parse data. Please check format.');
    }
  }, [filterOptions]);

  // Update a single parsed session field
  const updateParsedSession = useCallback((index, field, value) => {
    setParsedSessions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate derived fields based on what changed
      if (field === 'childId') {
        const child = filterOptions.children.find(c => c.id === value);
        updated[index].childName = child?.name || updated[index].childName;
        updated[index].childMatched = !!value;
      }
      if (field === 'observerId') {
        const observer = filterOptions.observers.find(o => o.id === value);
        updated[index].observerName = observer?.name || updated[index].observerName;
        updated[index].observerMatched = !!value;
      }
      if (field === 'durationMinutes') {
        const duration = parseInt(value) || 0;
        updated[index].durationMinutes = duration;
        updated[index].observerEarnings = duration * OBSERVER_RATE_PER_MIN;
        updated[index].totalEarnings = updated[index].observerEarnings + updated[index].principalEarnings;
      }
      if (field === 'observerEarnings') {
        updated[index].observerEarnings = parseFloat(value) || 0;
        updated[index].totalEarnings = updated[index].observerEarnings + updated[index].principalEarnings;
      }
      if (field === 'principalEarnings') {
        updated[index].principalEarnings = parseFloat(value) || 0;
        updated[index].totalEarnings = updated[index].observerEarnings + updated[index].principalEarnings;
      }
      
      // Update hasData check
      updated[index].hasData = !!(
        updated[index].childId && 
        updated[index].observerId && 
        updated[index].sessionDate
      );
      
      return updated;
    });
  }, [filterOptions]);

  // Remove a parsed session from list
  const removeParsedSession = useCallback((index) => {
    setParsedSessions(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Import all parsed sessions
  const importBulkSessions = useCallback(async () => {
    const validSessions = parsedSessions.filter(s => s.childId && s.observerId && s.sessionDate);
    
    if (validSessions.length === 0) {
      toast({ title: "Error", description: "No valid sessions to import. Please fill required fields.", variant: "destructive" });
      return false;
    }
    
    setBulkImporting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const session of validSessions) {
      try {
        const token = localStorage.getItem('principal_token');
        await axios.post(`${BACKEND_URL}/api/principal/session`, null, {
          params: {
            token,
            child_id: session.childId,
            observer_id: session.observerId,
            session_date: session.sessionDate,
            started_at: `${session.sessionDate}T09:00:00`,
            ended_at: `${session.sessionDate}T09:${String(Math.min(59, session.durationMinutes)).padStart(2, '0')}:00`,
            duration_minutes: session.durationMinutes,
            earnings: session.observerEarnings,
            notes: `Bulk imported. Principal earnings: ₹${session.principalEarnings}`,
            transcript: session.transcript,
            mood_rating: 3,
            topics_discussed: '',
            key_observations: session.report ? 'See AI report' : '',
            concerns: ''
          }
        });
        successCount++;
      } catch (error) {
        console.error('Failed to import session:', error);
        failCount++;
      }
    }
    
    setBulkImporting(false);
    toast({ 
      title: "Import Complete", 
      description: `${successCount} sessions imported successfully${failCount > 0 ? `, ${failCount} failed` : ''}` 
    });
    
    if (successCount > 0) {
      setBulkPasteData('');
      setParsedSessions([]);
      onImportComplete?.();
      return true;
    }
    
    return false;
  }, [parsedSessions, toast, onImportComplete]);

  // Reset state
  const resetBulkImport = useCallback(() => {
    setBulkPasteData('');
    setParsedSessions([]);
    setParseError('');
  }, []);

  return {
    // State
    bulkPasteData,
    parsedSessions,
    parseError,
    bulkImporting,
    // Actions
    setBulkPasteData,
    parseBulkPaste,
    updateParsedSession,
    removeParsedSession,
    importBulkSessions,
    resetBulkImport
  };
};

export default useBulkPasteImport;
